import { rd, RemoteData } from '@passionware/monads';
import { DimmedContainer } from 'v4/shared/components/forms/input/primitvies/DimmedContainer';
import {
  AbstractFilterWidget,
  AbstractFilterWidgetOperatorTrigger,
  AbstractFilterWidgetValueTrigger,
  FilterWidgetPropsBase
} from './_private/AbstractFilterWidget';
import React, { forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger
} from 'v5/design-system/Select';
import { useTranslation } from 'react-i18next';
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from 'v5/design-system/Popover';
import { InputGroup, InputGroupInput } from 'v5/design-system/InputGroup';
import { Button } from 'v5/design-system/Button';
import { useForm } from 'react-hook-form';
import { StringFilter } from 'v5/api/_common/query/filters/StringFilter';
import {
  Combobox,
  ComboboxContent,
  ComboboxError,
  ComboboxGroup,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  ComboboxLoading,
  ComboboxQueryRenderer,
  ComboboxTrigger
} from 'v5/design-system/Combobox';
import type {
  StringFilterSuggestion,
  StringFilterWidgetConfig
} from './FilterWidget.types';

type SuggestionsRemote = (
  search: string
) => RemoteData<StringFilterSuggestion[]>;

/** Renders suggestion RemoteData; keeps `suggestionsRemote` (incl. geolocation hooks) in a real component. */
function StringFilterSuggestionsList({
  search,
  onSelectSuggestion,
  suggestionsRemote
}: {
  search: string;
  /** Called when a row is chosen (click or keyboard); must apply `suggestion.value` to the filter. */
  onSelectSuggestion: (suggestionValue: string) => void;
  suggestionsRemote: SuggestionsRemote;
}) {
  const { t } = useTranslation('patterns', {
    keyPrefix: 'StringFilterWidget'
  });
  const suggestionsRd = suggestionsRemote(search);

  const typedText = search.trim();
  const renderItems = (suggestions: StringFilterSuggestion[]) => {
    const showRecentHeading = typedText === '' && suggestions.length > 0;
    const offerTypedText =
      typedText !== '' && !suggestions.some(s => s.value === typedText);
    return (
      <ComboboxGroup
        heading={showRecentHeading ? t('recentPlacesHeading') : undefined}
      >
        {suggestions.map((suggestion, index) => (
          <ComboboxItem
            key={`${suggestion.value}-${index}`}
            compact
            value={`${suggestion.value}-${index}`}
            onSelect={() => {
              onSelectSuggestion(suggestion.value);
            }}
          >
            {suggestion.label}
          </ComboboxItem>
        ))}
        {offerTypedText && (
          <ComboboxItem
            compact
            value={`typed:${typedText}`}
            onSelect={() => {
              onSelectSuggestion(typedText);
            }}
          >
            {t('useTypedText', {
              query: typedText,
              interpolation: { escapeValue: false }
            })}
          </ComboboxItem>
        )}
      </ComboboxGroup>
    );
  };

  return (
    <DimmedContainer shouldDim={rd.isPlaceholderData(suggestionsRd)}>
      {rd
        .fullJourney(suggestionsRd)
        .initially(
          <div className="px-2 py-2 text-center text-sm text-fg-muted">
            {t('suggestionsIdleHint')}
          </div>
        )
        .wait(<ComboboxLoading />)
        .catch(e => {
          console.error('StringFilterWidget suggestions error', e);
          return (
            <>
              <ComboboxError />
              {renderItems([])}
            </>
          );
        })
        .map(renderItems)}
    </DimmedContainer>
  );
}

export interface StringFilterWidgetProps
  extends FilterWidgetPropsBase<StringFilter> {
  filterField?: string;
  /** Same pattern as {@link EnumFilterWidget} `config` — from `FilterStringConfig.options`. */
  config?: StringFilterWidgetConfig;
}

const StringFilterWidgetInner = (
  {
    filterName,
    filterIcon,
    onRemove,
    value,
    onValueChange,
    config,
    filterField,
    ...rest
  }: StringFilterWidgetProps,
  ref: React.Ref<HTMLDivElement>
) => {
  const placeholder = config?.placeholder;
  const useSuggestions = config?.useSuggestions;
  const offers = (operator: StringFilter['operator']) =>
    config?.operators?.includes(operator) ?? true;
  /**
   * When Enter selects a cmdk item, the input also receives Enter; we mark a suggestion pick
   * so the input handler does not overwrite with partial query text (see cmdk + ComboboxItem).
   */

  const { t } = useTranslation('patterns', {
    keyPrefix: 'StringFilterWidget'
  });
  const { t: tPatterns } = useTranslation('patterns');

  const form = useForm<{ text: string }>({
    defaultValues: {
      text: value.value
    }
  });

  const [open, setOpen] = useState(false);
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  const watchedText = form.watch('text');

  useEffect(() => {
    if (!open) return;
    form.reset({
      text: value.value
    });
  }, [open, value, form]);

  const trimmedValue = watchedText.trim();
  const canApply = trimmedValue.length > 0;

  const submitForm = form.handleSubmit(formValues => {
    const nextValue = formValues.text.trim();
    if (!nextValue) return;
    onValueChange({
      operator: value.operator,
      value: nextValue
    });
    setOpen(false);
  });

  const handleInputKeyDown: React.KeyboardEventHandler<
    HTMLInputElement
  > = e => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      void submitForm();
    }
  };

  const renderValueNode = useMemo(() => {
    if (!value.value) {
      return <span className="text-fg-muted">{t('emptyValue')}</span>;
    }
    return <span>{value.value}</span>;
  }, [value.value, t]);

  return (
    <AbstractFilterWidget
      ref={ref}
      filterName={filterName}
      filterIcon={filterIcon}
      onRemove={onRemove}
      filterField={filterField}
      {...rest}
      operatorSlot={
        <Select
          value={value.operator}
          onValueChange={next =>
            onValueChange({
              operator: next as StringFilter['operator'],
              value: value.value
            })
          }
        >
          <SelectTrigger asChild>
            <AbstractFilterWidgetOperatorTrigger>
              {
                {
                  equals: t('operator.equals'),
                  contains: t('operator.contains'),
                  notContains: t('operator.notContains')
                }[value.operator]
              }
            </AbstractFilterWidgetOperatorTrigger>
          </SelectTrigger>
          <SelectContent>
            {offers('equals') && (
              <SelectItem value="equals">{t('operator.equals')}</SelectItem>
            )}
            {offers('contains') && (
              <SelectItem value="contains">{t('operator.contains')}</SelectItem>
            )}
            {offers('notContains') && (
              <SelectItem value="notContains">
                {t('operator.notContains')}
              </SelectItem>
            )}
          </SelectContent>
        </Select>
      }
      valueSlot={
        useSuggestions ? (
          <Combobox
            open={suggestionsOpen}
            onOpenChange={setSuggestionsOpen}
            defaultQuery={value.value}
          >
            <ComboboxTrigger asChild>
              <AbstractFilterWidgetValueTrigger data-element="value-trigger">
                {renderValueNode}
              </AbstractFilterWidgetValueTrigger>
            </ComboboxTrigger>
            <ComboboxContent className="max-h-[50vh]">
              <ComboboxInput
                placeholder={
                  placeholder ?? tPatterns('EnumFilterWidget.searchPlaceholder')
                }
                autoComplete="off"
              />
              <ComboboxList>
                <ComboboxQueryRenderer
                  emptyContent={<>{t('suggestionsEmpty')}</>}
                >
                  {search =>
                    rd.of(
                      <StringFilterSuggestionsList
                        search={search}
                        onSelectSuggestion={nextValue => {
                          onValueChange({
                            operator: value.operator,
                            value: nextValue
                          });
                        }}
                        suggestionsRemote={useSuggestions!}
                      />
                    )
                  }
                </ComboboxQueryRenderer>
              </ComboboxList>
            </ComboboxContent>
          </Combobox>
        ) : (
          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
              <AbstractFilterWidgetValueTrigger data-element="value-trigger">
                {renderValueNode}
              </AbstractFilterWidgetValueTrigger>
            </PopoverTrigger>
            <PopoverContent
              className="p-2 w-72"
              align="start"
              onInteractOutside={() => {
                void submitForm();
              }}
            >
              <form className="contents" onSubmit={submitForm}>
                <InputGroup variant="regular" size="sm">
                  <InputGroupInput
                    type="text"
                    placeholder={placeholder ?? t('placeholder')}
                    {...form.register('text')}
                    onKeyDown={handleInputKeyDown}
                    autoComplete="off"
                  />
                </InputGroup>
                <div className="mt-2 flex justify-end gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    type="button"
                    onClick={() => setOpen(false)}
                  >
                    {t('cancel')}
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    type="submit"
                    disabled={!canApply}
                  >
                    {t('apply')}
                  </Button>
                </div>
              </form>
            </PopoverContent>
          </Popover>
        )
      }
    />
  );
};

export const StringFilterWidget = forwardRef(StringFilterWidgetInner) as (
  props: StringFilterWidgetProps & { ref?: React.Ref<HTMLDivElement> }
) => ReturnType<typeof StringFilterWidgetInner>;
